import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  DeliveryStatus,
  DeliveryStatusAux,
} from "src/app/models/delivery.schema";
import { Partner } from "src/app/models/partner.schema";
import { PartnerService } from "src/app/services/partner.service";
import { SpinnerService } from "src/app/services/spinner.service";
import {
  PartnerActivityReminderService,
  PartnerActivityReminderSettingSchema,
} from "./../../services/partner-activity-reminder.service";

interface DeliveryStatusOption {
  label: string;
  value: DeliveryStatus;
}

@Component({
  selector: "app-partner-activity-reminder-settings",
  templateUrl: "./partner-activity-reminder-settings.component.html",
  styleUrls: ["./partner-activity-reminder-settings.component.scss"],
})
export class PartnerActivityReminderSettingsComponent implements OnInit {
  form!: FormGroup;

  loading = true;
  saving = false;
  successMessage = "";
  errorMessage = "";

  partners: Partner[] = [];

  readonly deliveryStatuses: DeliveryStatusOption[] =
    this.getDeliveryStatusOptions();

  readonly timezones = ["Europe/Rome", "UTC", "Asia/Kolkata"];

  constructor(
    private readonly fb: FormBuilder,
    private readonly partnerService: PartnerService,
    private readonly spinnerService: SpinnerService,
    private readonly partnerActivityReminderService: PartnerActivityReminderService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.buildForm();
    this.listenToEnabledChanges();

    try {
      this.loading = true;
      this.spinnerService.show();

      await this.loadPartners();
      await this.loadSettings();
    } catch (error) {
      console.error("ERROR", error);
      this.errorMessage =
        "Impossibile caricare le impostazioni del promemoria.";
    } finally {
      this.loading = false;
      this.spinnerService.hide();
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      enabled: [true],
      reminderTime: ["09:00", Validators.required],
      timezone: ["Europe/Rome", Validators.required],
      daysAhead: [
        7,
        [Validators.required, Validators.min(1), Validators.max(30)],
      ],
      statuses: [this.getDefaultStatuses(), Validators.required],
      subject: ["Riepilogo attività", Validators.required],
    });
  }

  private listenToEnabledChanges(): void {
    this.form.get("enabled")?.valueChanges.subscribe((enabled: boolean) => {
      this.setFieldsEnabled(enabled);
    });
  }

  private setFieldsEnabled(enabled: boolean): void {
    const fields = [
      "reminderTime",
      "timezone",
      "daysAhead",
      "statuses",
      "subject",
    ];

    fields.forEach((field) => {
      const control = this.form.get(field);

      if (!control) {
        return;
      }

      if (enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  private getDefaultStatuses(): DeliveryStatus[] {
    return DeliveryStatusAux.active.map((status) => status as DeliveryStatus);
  }

  private getDeliveryStatusOptions(): DeliveryStatusOption[] {
    return DeliveryStatusAux.active.map((status) => {
      const deliveryStatus = status as DeliveryStatus;
      const aux = DeliveryStatusAux.aux[deliveryStatus];

      return {
        value: deliveryStatus,
        label: aux?.ln?.it || deliveryStatus,
      };
    });
  }

  private async loadPartners(): Promise<void> {
    const response = await this.partnerService.getAllActivePartnersForDelivery();
    this.partners = response?.partners || [];
  }

  async loadSettings(): Promise<void> {
    this.successMessage = "";
    this.errorMessage = "";

    const settings = await this.partnerActivityReminderService.getSettings();

    this.form.patchValue({
      enabled: settings.enabled,
      reminderTime: settings.reminderTime,
      timezone: settings.timezone,
      daysAhead: settings.daysAhead,
      statuses: settings.statuses?.length
        ? settings.statuses
        : this.getDefaultStatuses(),
      subject: settings.subject,
    });

    this.setFieldsEnabled(settings.enabled);
  }

  isStatusSelected(status: DeliveryStatus): boolean {
    const statuses: DeliveryStatus[] = this.form?.get("statuses")?.value || [];
    return statuses.includes(status);
  }

  toggleStatus(status: DeliveryStatus): void {
    if (!this.enabled) {
      return;
    }

    const control = this.form.get("statuses");

    if (!control) {
      return;
    }

    const currentStatuses: DeliveryStatus[] = control.value || [];
    const exists = currentStatuses.includes(status);

    const nextStatuses = exists
      ? currentStatuses.filter((item) => item !== status)
      : [...currentStatuses, status];

    control.setValue(nextStatuses);
    control.markAsDirty();
    control.markAsTouched();
  }

  async save(): Promise<void> {
    this.successMessage = "";
    this.errorMessage = "";

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = "Correggi gli errori di validazione.";
      return;
    }

    const payload: PartnerActivityReminderSettingSchema =
      this.form.getRawValue();

    if (payload.enabled && payload.statuses.length === 0) {
      this.errorMessage = "Seleziona almeno uno stato della consegna.";
      return;
    }

    this.saving = true;

    try {
      const settings =
        await this.partnerActivityReminderService.updateSettings(payload);

      this.form.patchValue({
        enabled: settings.enabled,
        reminderTime: settings.reminderTime,
        timezone: settings.timezone,
        daysAhead: settings.daysAhead,
        statuses: settings.statuses?.length
          ? settings.statuses
          : this.getDefaultStatuses(),
        subject: settings.subject,
      });

      this.setFieldsEnabled(settings.enabled);

      this.successMessage =
        "Impostazioni del promemoria attività partner salvate.";
    } catch (error) {
      console.error(error);
      this.errorMessage =
        "Impossibile salvare le impostazioni del promemoria.";
    } finally {
      this.saving = false;
    }
  }

  get enabled(): boolean {
    return this.form?.get("enabled")?.value ?? false;
  }
}